import { z } from 'zod';

const KB = 1024;
const MB = 1024 * KB;

/**
 * What each game may use, per environment (decided Oct 25, 2026; PRD 5.6). Roomy for normal
 * web games and safe for one shared VM; admins raise them per game with `set-quota`.
 */
export const GAME_QUOTAS = {
  /** Cloud saves one player can have in one game. */
  savesPerPlayer: 20,
  /** One save's size, as JSON. */
  saveBytes: 64 * KB,
  /** One game data document's size, as JSON. */
  documentBytes: 16 * KB,
  /** Game data documents one player can have in one game (player collections). */
  documentsPerPlayer: 100,
  /** Game-wide game data documents in one game (game collections). */
  documentsPerGame: 10_000,
  /** The game's remote config, as JSON. */
  configBytes: 64 * KB,
  /** Game Services calls a minute from one player in one game. */
  callsPerMinute: 120,
  /** Scores one player can send to one leaderboard in a minute. */
  scoresPerMinute: 10,
  /** Leaderboards one game can have. */
  leaderboards: 20,
  /** Achievements one game can have. */
  achievements: 100,
  /** Multiplayer modes one game can have. */
  modes: 10,
  /** Live events one game can have (M37), test and live each. */
  events: 20,
  /** Schedule rules one game can have (M37), test and live each. */
  rules: 20,
  /** Everything a game stores in the database: saves and game data. */
  storageBytes: 200 * MB,
} as const;

export type GameQuotaName = keyof typeof GAME_QUOTAS;
export type GameQuotas = Record<GameQuotaName, number>;

/** Highest value an admin can raise each quota to. */
export const GAME_QUOTA_CEILINGS: GameQuotas = {
  savesPerPlayer: 200,
  // A save, document or config travels in one request; the API takes bodies up to 1 MB.
  saveBytes: 900 * KB,
  documentBytes: 900 * KB,
  documentsPerPlayer: 10_000,
  documentsPerGame: 1_000_000,
  configBytes: 900 * KB,
  callsPerMinute: 1200,
  scoresPerMinute: 600,
  leaderboards: 100,
  achievements: 1000,
  modes: 50,
  events: 100,
  rules: 100,
  storageBytes: 20 * 1024 * MB,
};

/** Per-game raises, stored on the project. */
export const quotaOverridesSchema = z
  .object(
    Object.fromEntries(
      Object.entries(GAME_QUOTA_CEILINGS).map(([name, ceiling]) => [
        name,
        z.number().int().positive().max(ceiling).optional(),
      ]),
    ) as Record<GameQuotaName, z.ZodOptional<z.ZodNumber>>,
  )
  .strict();
export type QuotaOverrides = z.infer<typeof quotaOverridesSchema>;

/** A game's quotas: the defaults with its raises on top. */
export function effectiveQuotas(overrides: QuotaOverrides | null | undefined): GameQuotas {
  return { ...GAME_QUOTAS, ...overrides };
}

/** Save names: letters, digits, `_`, `-`, `.` and `:`, up to 64 characters. */
export const SAVE_KEY_PATTERN = /^[A-Za-z0-9_.:-]{1,64}$/;
export const saveKeySchema = z
  .string()
  .regex(
    SAVE_KEY_PATTERN,
    'Use up to 64 letters, digits, dots, colons, dashes or underscores for a save’s name',
  );

/** Size of a value as stored: its JSON text in UTF-8. */
export function jsonBytes(value: unknown): number {
  return new TextEncoder().encode(JSON.stringify(value)).length;
}

/** One of a player's saves, as `save.list()` describes it (without its contents). */
export interface SaveInfo {
  key: string;
  bytes: number;
  updatedAt: string;
}

/** Bridge parameters for the `save.*` methods, checked by mogzo.com before calling the API. */
export const saveKeyParamsSchema = z.object({ key: saveKeySchema }).strict();
export const saveSetParamsSchema = z.object({ key: saveKeySchema, value: z.json() }).strict();

/**
 * Game data collections (PRD 5.2). `game`: documents every player of the game can read; a
 * player may add one, and only its author (or the developer, in the dashboard) changes it.
 * `player`: each player's own documents, private to them.
 */
export const DATA_SCOPES = ['game', 'player'] as const;
export type DataScope = (typeof DATA_SCOPES)[number];

/** Collection names: lowercase letters, digits, `_` or `-`, starting with a letter. */
export const COLLECTION_NAME_PATTERN = /^[a-z][a-z0-9_-]{0,39}$/;
export const collectionNameSchema = z
  .string()
  .regex(
    COLLECTION_NAME_PATTERN,
    'Use up to 40 lowercase letters, digits, dashes or underscores for a collection, starting with a letter',
  );
/** Document ids follow the same rules as save names. */
export const documentIdSchema = z
  .string()
  .regex(
    SAVE_KEY_PATTERN,
    'Use up to 64 letters, digits, dots, colons, dashes or underscores for a document’s id',
  );

const fieldName = z
  .string()
  .regex(/^[A-Za-z_][A-Za-z0-9_]{0,39}$/, 'Query fields are top-level names like level or score');

/**
 * A game data query: documents whose top-level fields equal `where`'s values, sorted by one
 * field (`-score` for highest first), a page at a time.
 */
export const dataQuerySchema = z
  .object({
    where: z
      .record(fieldName, z.union([z.string().max(200), z.number(), z.boolean(), z.null()]))
      .refine((where) => Object.keys(where).length <= 5, 'Use up to 5 fields in where')
      .optional(),
    orderBy: z
      .string()
      .regex(/^-?[A-Za-z_][A-Za-z0-9_]{0,39}$/, 'orderBy is a field name, with - for descending')
      .optional(),
    limit: z.number().int().min(1).max(100).default(20),
    offset: z.number().int().min(0).max(1000).default(0),
    /** Game collections only: just the documents this player made. */
    mine: z.boolean().optional(),
  })
  .strict();
export type DataQuery = z.input<typeof dataQuerySchema>;

/** A game data document as the game sees it. */
export interface DataDocument {
  id: string;
  data: unknown;
  /** True when this player made it (game collections) or owns it (player collections). */
  mine: boolean;
  updatedAt: string;
}

/** Bridge parameters for the `data.*` methods. */
const collectionParams = {
  collection: collectionNameSchema,
  scope: z.enum(DATA_SCOPES).default('game'),
};
export const dataDocParamsSchema = z.object({ ...collectionParams, id: documentIdSchema }).strict();
export const dataSetParamsSchema = z
  .object({ ...collectionParams, id: documentIdSchema, value: z.json() })
  .strict();
export const dataQueryParamsSchema = z
  .object({ ...collectionParams, query: dataQuerySchema.default({ limit: 20, offset: 0 }) })
  .strict();
